import type { StorefrontCategoryNode } from '@erp/shared';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { publicApi } from '../../lib/publicApi.js';

import { HomeSkeleton } from './StorefrontSkeletons.js';
import { StoreImage } from './StoreImage.js';

/** Browse-all-categories index — a grid of every published category. */
export function StorefrontCategoriesPage(): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const { slug, site } = useStorefront();
  const [cats, setCats] = useState<StorefrontCategoryNode[] | null>(null);

  useEffect(() => {
    setCats(null);
    void publicApi().get<{ items: StorefrontCategoryNode[] }>(`/${slug}/categories`).then((r) => setCats(r.data.items)).catch(() => setCats([]));
  }, [slug, i18n.language]);

  return (
    <div className="flex flex-col gap-6">
      <Helmet><title>{`${t('store.categories.title', { defaultValue: 'Categories' })} · ${site.storeName}`}</title></Helmet>
      <header>
        <h1 className="text-3xl font-bold tracking-tight">{t('store.categories.title', { defaultValue: 'Categories' })}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('store.categories.subtitle', { defaultValue: 'Browse our products by category.' })}</p>
      </header>

      {!cats ? (
        <HomeSkeleton />
      ) : cats.length === 0 ? (
        <div className="py-16 text-center text-fg-muted">{t('store.categories.empty', { defaultValue: 'No categories yet.' })}</div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {cats.map((c) => {
            return (
              <Link key={c.id} to={`/s/${slug}/category/${c.slug}`} className="group relative aspect-[4/3] overflow-hidden rounded-2xl border border-border bg-bg-subtle transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-1 hover:shadow-lg active:scale-[0.99] motion-reduce:transform-none">
                <StoreImage src={c.imageUrl} alt={c.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-110 motion-reduce:transform-none" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" aria-hidden />
                <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                  <div className="truncate text-sm font-semibold drop-shadow-sm">{c.name}</div>
                  <div className="text-xs opacity-90">{t('store.category.count', { count: c.productCount })}</div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
